import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import './PastorDetail.css';

interface PastorData {
  name: string;
  title: string;
  image: string;
  alt: string;
  scripture: string;
  scriptureRef: string;
  bio: string[];
  educationLabel: string;
  education: string;
}

const PastorDetail: React.FC = () => {
  const { pastorId } = useParams<{ pastorId: string }>();
  const navigate = useNavigate();

  const pastors: Record<string, PastorData> = {
    barmond: {
      name: 'Apostle Brian A. Harris',
      title: 'Founder & Lead Pastor',
      image: '/images/pastor_pic.jpg',
      alt: 'Apostle Brian A. Harris - Founder & Lead Pastor',
      scripture: '"Go ye therefore, and teach all nations, baptizing them in the name of the Father, and of the Son, and of the Holy Ghost: Teaching them to observe all things whatsoever I have commanded you: and, lo, I am with you always, even unto the end of the world. Amen."',
      scriptureRef: 'Matthew 28:19-20 (KJV)',
      bio: [
        'Apostle Brian A. Harris, affectionately called "Pastor B," is the Founder and Lead Pastor of Gener8ions Church in North Little Rock, AR. Apostle Harris is a second generational preacher of the Gospel of Jesus Christ. He has been preaching for over 32 years, beginning at the age of 17 years old.',
        'Apostle Brian, now in his 50\'s, has answered the unique call of living the multi-dimensional life. He has declared that God has not called him to do everything, but He will do everything that God has called him to do. He has declared war against every limitation that has been placed on his life whether by people or self-imposed limitations. At this stage in Apostle Brian\'s life, he has decided that he will not meet Jesus without returning interest to God on his investment in his life.',
        'Apostle Brian is a church planter, preacher, teacher, educator, mentor, leader, scholar, aspiring author, podcast host, conference host, visionary, musician and worshiper of Jesus Christ. Apostle Harris holds a Bachelor of Science in Religion: Biblical and Theological Studies degree from Liberty University, Rawlings School of Divinity in Lynchburg, VA. He is presently pursuing his Masters of Divinity (MDiv) from Liberty Theological Seminary, Lynchburg, VA. After graduation, he is seeking to pursue his Doctorate in Ministry (DMin) and dedicate the rest of his life continuing to preach, teach and train the next generation of church leadership.',
        'Apostle Brian is the CEO / Founder of Brian A. Harris Ministries. Through Brian A. Harris Ministries, M4M (Mentoring For Ministry) was born. It is a ministry that develops, equips and encourage those who have answered the call of God on their lives and are serious about growing in their spiritual formation and ministry practice.',
        'Apostle Brian is a Senior High School English IV teacher at Memphis Rise Academy in Memphis, TN. Apostle Brian is convinced and convicted this is an extension of his ministry. Each day he seeks to make a difference in the lives of Senior High School students through education, mentorship, sponsoring the Christian Student Association and Content Creation after school clubs. Apostle Brian ensures his students that he prays for them daily and has developed a healthy bond with his students, that makes it safe for his students to share their convictions of their own faith and is necessary for academic achievement. Apostle Brian\'s students unapologetically say, that "Mr. Harris is their favorite teacher."',
        'Apostle Brian is married to Dr. Kimberly R. Harris with whom he shares one beautiful daughter, Kianna. Apostle Harris is passionate about God, his family, his ministry, his education, and his career. He believes in the operation of the gifts of the Spirit as well as, bearing the fruit of the Spirit. He believes charisma without character is a road to disaster. He believes in every believer becoming a disciple of Jesus Christ and discipling others to follow Christ (Matthew 28:19-20).'
      ],
      educationLabel: 'Academic Achievements',
      education: 'Bachelor of Science in Religion at Liberty University, Rawlings School of Divinity; Master of Divinity (MDiv) in Biblical Studies at Liberty Theological Seminary (Currently Enrolled)'
    },
    kimberly: {
      name: 'Dr. Kimberly R. Harris',
      title: 'Co-founder & Executive Pastor',
      image: '/images/dr_kimberly_harris.PNG',
      alt: 'Dr. Kimberly R. Harris - Co-founder & Executive Pastor',
      scripture: '"We are therefore Christ\'s ambassadors, as though God were making his appeal through us. We implore you on Christ\'s behalf: Be reconciled to God."',
      scriptureRef: '2 Corinthians 5:20 (KJV)',
      bio: [
        'Co-founder and Executive Pastor of Gener8ions Church in N. Little Rock, AR, Dr. Harris has served diligently in ministry for nearly 15 years, overseeing several ministries within the organization including Teachers and Ministers training, Emerge Student Ministry, Intercessory Prayer, and Media.',
        'Pastor, teacher, and ministry and marketplace leader, Dr. Harris holds a Doctor of Philosophy degree in Health Science (University of Arkansas), a Master of Divinity (Liberty University), a Master of Science in Kinesiology (California University of Pennsylvania), and a Bachelor of Science in Education (University of Arkansas). Dr. Harris is an avid proponent of education and former adjunct instructor at Concordia University – Chicago and University of Arkansas. She currently leads a team of research professionals in cancer clinical trials for the Leukemia/Lymphoma Division of St. Jude Children\'s Research Hospital in Memphis, Tennessee.',
        'Dr. Harris is married to Apostle Brian A. Harris with whom she shares one beautiful daughter, Kianna. She is an anointed teacher of the Word of God who greatly enjoys focusing on the ministry of love within her family and allowing that to overflow into her passion to love and lead others into the way of Christ.',
        'The Table – under the umbrella of Dr. Kimberly R. Harris Ministries – is a women\'s ministry that God has given her to extend not only to the women of Gener8ions Church but to women across the region, the nation, and the world.',
        'In the Bible, the table is a symbol of family, community, and faithful presence. Tables are where we gather for celebration and connection and can represent fellowship, provision, covenant, and sometimes judgment. Ultimately, the table is a place where believers are invited into a deeper relationship with God.',
        'Stay on the lookout for opportunities to pull up a seat at The Table — video devotionals, podcasts, small groups, online Bible studies, retreats, 1:1 personal discipleship, and health & wellness.'
      ],
      educationLabel: 'Education',
      education: 'Ph.D. in Health Science (University of Arkansas); M.Div. (Liberty University); M.S. in Kinesiology (California University of Pennsylvania); B.S. in Education (University of Arkansas)'
    }
  };

  const pastor = pastorId ? pastors[pastorId] : null;

  if (!pastor) {
    return (
      <div className="pastor-detail-page">
        <div className="pastor-not-found">
          <h1>Pastor Not Found</h1>
          <button onClick={() => navigate('/about')} className="btn btn-primary">
            Return to About
          </button>
        </div>
      </div>
    );
  }

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="pastor-detail-page">
      <div className="pastor-detail-hero">
        <motion.div
          className="pastor-detail-hero-content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <button onClick={() => navigate('/about')} className="back-button">
            ← Back to About
          </button>
          <div className="pastor-hero-info">
            <div className="pastor-hero-image-container">
              <img src={pastor.image} alt={pastor.alt} className="pastor-hero-image" />
            </div>
            <div className="pastor-hero-text">
              <h1>{pastor.name}</h1>
              <p className="pastor-hero-title">{pastor.title}</p>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="pastor-detail-container">
        <motion.div
          className="pastor-detail-content"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: "-100px" }}
          variants={{
            animate: {
              transition: {
                staggerChildren: 0.1
              }
            }
          }}
        >
          <motion.div className="pastor-bio-section" variants={fadeInUp}>
            <h2>About {pastor.name.split(' ')[pastor.name.split(' ').length - 1]}</h2>
            <div className="pastor-bio">
              {pastor.bio.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </motion.div>

          <motion.div className="pastor-education" variants={fadeInUp}>
            <h3>{pastor.educationLabel}</h3>
            <p>{pastor.education}</p>
          </motion.div>

          <motion.blockquote className="pastor-scripture-full" variants={fadeInUp}>
            {pastor.scripture}
            <cite>— {pastor.scriptureRef}</cite>
          </motion.blockquote>
        </motion.div>
      </div>
    </div>
  );
};

export default PastorDetail;


